/**
 * Image URL utilities for memory-efficient image loading.
 * Uses Cloudinary transformations to serve appropriately-sized images
 * instead of loading full-resolution originals everywhere.
 */

/**
 * Apply Cloudinary transformations to an image URL for thumbnails/previews.
 * Only transforms Cloudinary URLs; returns non-Cloudinary URLs unchanged.
 *
 * @param {string} url - Original image URL
 * @param {Object} options - Transformation options
 * @param {number} options.width - Target width (default: 200)
 * @param {number} options.height - Target height (default: 200)
 * @param {string} options.crop - Crop mode (default: 'fill')
 * @param {string} options.quality - Quality (default: 'auto')
 * @param {string} options.format - Format (default: 'auto')
 * @returns {string} Transformed URL or original URL
 */
export const getOptimizedImageUrl = (url, options = {}) => {
  if (!url) return url;

  const {
    width = 200,
    height = 200,
    crop = 'fill',
    quality = 'auto',
    format = 'auto'
  } = options;

  // Only transform Cloudinary URLs
  if (!url.includes('res.cloudinary.com')) {
    return url;
  }

  // Build transformation string
  const transform = `c_${crop},w_${width},h_${height},q_${quality},f_${format}`;

  // Insert transformation before /upload/ or after existing transforms
  // Pattern: .../upload/v1234/filename -> .../upload/c_fill,.../v1234/filename
  return url.replace(
    /\/upload\/(v\d+\/)/,
    `/upload/${transform}/$1`
  );
};

/**
 * Get a thumbnail URL for grid/card display (400x400 covers 3x retina for 140px cards)
 */
export const getThumbnailUrl = (url) => {
  return getOptimizedImageUrl(url, {
    width: 400,
    height: 400,
    crop: 'fill',
    quality: 'auto',
    format: 'auto'
  });
};

/**
 * Get a preview URL for medium display (~400x400)
 */
export const getPreviewUrl = (url) => {
  return getOptimizedImageUrl(url, {
    width: 600,
    height: 600,
    crop: 'limit',
    quality: 'auto',
    format: 'auto'
  });
};

/**
 * Get a small thumbnail URL for panels/lists (~100x100)
 */
export const getSmallThumbnailUrl = (url) => {
  return getOptimizedImageUrl(url, {
    width: 150,
    height: 150,
    crop: 'fill',
    quality: 'auto',
    format: 'auto'
  });
};
